// Ruting aplikacji – strony ładowane lazy (code splitting), mniejszy początkowy JS
import React, { Suspense, lazy, ComponentType } from 'react';
import { Route, Routes as RouterRoutes, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { isWiseUiEnabled } from 'config/wiseUi';
import useRoutes from './useRoutes';
import { useNotificationToSnackbar } from 'hooks';
import { DashboardSubLayout, WiseDashboardSubLayout, DefaultLayout, MainLayout, TraderPanelLayout, TraderPanelProtectedRoute } from 'components/layouts';

const DashboardSubLayoutPage: ComponentType<{ urlPrefix: string }> = isWiseUiEnabled()
  ? WiseDashboardSubLayout
  : DashboardSubLayout;
import { ManagementPanelLayout } from 'plugins/components/layouts';
import { WiseSetupGuard } from 'components/wise';
import {
  Agreements,
  ChangePasswordForm,
  NewVersionPopup
} from 'components/containers';
import { Modal } from 'components/controls';

const Home = lazy(() => import('pages/Home'));
const Login = lazy(() => import('pages/Login'));
const WiseLogin = lazy(() => import('pages/wise/WiseLogin'));
const LoginPage = isWiseUiEnabled() ? WiseLogin : Login;
const WisePublicOfferAcceptance = lazy(() => import('pages/wise/WisePublicOfferAcceptance'));
const ForgotPassword = lazy(() => import('pages/ForgotPassword'));
const ResetPassword = lazy(() => import('pages/ResetPassword'));
const DownloadExport = lazy(() => import('pages/DownloadExport'));
const Cart = lazy(() => import('pages/Cart'));
const WiseCart = lazy(() => import('pages/wise/WiseCart'));
const CartPage = isWiseUiEnabled() ? WiseCart : Cart;
const Checkout = lazy(() => import('pages/Checkout'));
const WiseCheckout = lazy(() => import('pages/wise/WiseCheckout'));
const CheckoutPage = isWiseUiEnabled() ? WiseCheckout : Checkout;
const Dashboard = lazy(() => import('pages/Dashboard'));
const WiseDashboard = lazy(() => import('pages/wise/WiseDashboard'));
const DashboardPage = isWiseUiEnabled() ? WiseDashboard : Dashboard;
const Carts = lazy(() => import('pages/Carts'));
const WiseCarts = lazy(() => import('pages/wise/WiseCarts'));
const CartsPage = isWiseUiEnabled() ? WiseCarts : Carts;
const WiseCartsToApprove = lazy(() => import('pages/wise/WiseCartsToApprove'));
const WiseCartToApprove = lazy(() => import('pages/wise/WiseCartToApprove'));
const CartsImport = lazy(() => import('pages/CartsImport'));
const WiseCartsImport = lazy(() => import('pages/wise/WiseCartsImport'));
const CartsImportPage = isWiseUiEnabled() ? WiseCartsImport : CartsImport;
const Clients = lazy(() => import('pages/Clients'));
const Deliveries = lazy(() => import('pages/Deliveries'));
const WiseDeliveries = lazy(() => import('pages/wise/WiseDeliveries'));
const DeliveriesPage = isWiseUiEnabled() ? WiseDeliveries : Deliveries;
const Documents = lazy(() => import('pages/Documents'));
const WiseDocuments = lazy(() => import('pages/wise/WiseDocuments'));
const DocumentsPage = isWiseUiEnabled() ? WiseDocuments : Documents;
const Export = lazy(() => import('pages/Export'));
const WiseExport = lazy(() => import('pages/wise/WiseExport'));
const ExportPage = isWiseUiEnabled() ? WiseExport : Export;
const Faq = lazy(() => import('pages/Faq'));
const Consent = lazy(() => import('pages/Consent'));
const WiseConsent = lazy(() => import('pages/wise/WiseConsent'));
const ConsentPage = isWiseUiEnabled() ? WiseConsent : Consent;
const MyAccount = lazy(() => import('pages/MyAccount'));
const WiseMyAccount = lazy(() => import('pages/wise/WiseMyAccount'));
const MyAccountPage = isWiseUiEnabled() ? WiseMyAccount : MyAccount;
const OffersDedicated = lazy(() => import('pages/OffersDedicated'));
const WiseOffersDedicated = lazy(() => import('pages/wise/WiseOffersDedicated'));
const OffersDedicatedPage = isWiseUiEnabled() ? WiseOffersDedicated : OffersDedicated;
const ClientOfferDetails = lazy(() => import('pages/ClientOfferDetails'));
const WiseClientOfferDetails = lazy(() => import('pages/wise/WiseClientOfferDetails'));
const ClientOfferDetailsPage = isWiseUiEnabled() ? WiseClientOfferDetails : ClientOfferDetails;
const OffersPeriodic = lazy(() => import('pages/OffersPeriodic'));
const WiseOffersPeriodic = lazy(() => import('pages/wise/WiseOffersPeriodic'));
const OffersPeriodicPage = isWiseUiEnabled() ? WiseOffersPeriodic : OffersPeriodic;
const Orders = lazy(() => import('pages/Orders'));
const Order = lazy(() => import('pages/Order'));
const WiseOrderPage = lazy(() => import('pages/wise/WiseOrder'));
const OrderPage = isWiseUiEnabled() ? WiseOrderPage : Order;
const Payments = lazy(() => import('pages/Payments'));
const WisePayments = lazy(() => import('pages/wise/WisePayments'));
const PaymentsPage = isWiseUiEnabled() ? WisePayments : Payments;
const Receivers = lazy(() => import('pages/Receivers'));
const WiseReceivers = lazy(() => import('pages/wise/WiseReceivers'));
const ReceiversPage = isWiseUiEnabled() ? WiseReceivers : Receivers;
const ShoppingLists = lazy(() => import('pages/ShoppingLists'));
const WiseShoppingLists = lazy(() => import('pages/wise/WiseShoppingLists'));
const ShoppingListsPage = isWiseUiEnabled() ? WiseShoppingLists : ShoppingLists;
const Terms = lazy(() => import('pages/Terms'));
const WiseTerms = lazy(() => import('pages/wise/WiseTerms'));
const TermsPage = isWiseUiEnabled() ? WiseTerms : Terms;
const Users = lazy(() => import('pages/Users'));
const WiseUsers = lazy(() => import('pages/wise/WiseUsers'));
const UsersPage = isWiseUiEnabled() ? WiseUsers : Users;
const WiseCompany = lazy(() => import('pages/wise/WiseCompany'));
const CmsSections = lazy(() => import('pages/CmsSections'));
const CmsSection = lazy(() => import('pages/CmsSection'));
const CmsArticles = lazy(() => import('pages/CmsArticles'));
const CmsArticle = lazy(() => import('pages/CmsArticle'));
const CmsMedia = lazy(() => import('pages/CmsMedia'));
const CmsSliders = lazy(() => import('pages/CmsSliders'));
const CmsSlider = lazy(() => import('pages/CmsSlider'));
const UrlResolver = lazy(() => import('pages/UrlResolver'));
const WorkInProgress = lazy(() => import('pages/WorkInProgress'));
const Products = lazy(() => import('pages/Products'));
const WiseProducts = lazy(() => import('pages/wise/WiseProducts'));
const Product = lazy(() => import('pages/Product'));
const WiseProduct = lazy(() => import('pages/wise/WiseProduct'));
const Register = lazy(() => import('pages/Register'));
const WiseRegister = lazy(() => import('pages/wise/WiseRegister'));
const RegisterPage = isWiseUiEnabled() ? WiseRegister : Register;
const ProductPage = isWiseUiEnabled() ? WiseProduct : Product;
const ProductsPage = isWiseUiEnabled() ? WiseProducts : Products;
const RegisterEmailConfirm = lazy(() => import('pages/RegisterEmailConfirm'));
const ThankYouRegister = lazy(() => import('pages/ThankYouRegister'));
const PaymentSuccess = lazy(() => import('pages/PaymentSuccess'));
const PaymentStatus = lazy(() => import('pages/PaymentStatus'));
const PanelDashboard = lazy(() => import('pages/PanelDashboard'));
const PanelDynamicPage = lazy(() => import('pages/PanelDynamicPage'));
const PanelProductsImport = lazy(() => import('pages/PanelProductsImport'));
const PanelSetup = lazy(() => import('pages/PanelSetup'));
const PanelPages = lazy(() => import('pages/PanelPages'));
const Confirmation = lazy(() => import('pages/Checkout/components/Confirmation'));
const ReturnsComplaints = lazy(() => import('pages/ReturnsComplaints'));
const ReturnComplaint = lazy(() => import('pages/ReturnComplaint'));
const KnowledgeBaseHome = lazy(() => import('pages/KnowledgeBaseHome'));
const WiseKnowledgeBaseHome = lazy(() => import('pages/wise/WiseKnowledgeBaseHome'));
const KnowledgeBaseHomePage = isWiseUiEnabled() ? WiseKnowledgeBaseHome : KnowledgeBaseHome;
const KnowledgeBaseArticle = lazy(() => import('pages/KnowledgeBaseArticle'));
const WiseArticleBlog = lazy(() => import('pages/wise/WiseArticleBlog'));
const KnowledgeBaseArticlePage = isWiseUiEnabled() ? WiseArticleBlog : KnowledgeBaseArticle;
const KnowledgeBaseAll = lazy(() => import('pages/KnowledgeBaseAll'));
const PanelBlog = lazy(() => import('pages/PanelBlog'));
const BlogArticle = lazy(() => import('pages/BlogArticle'));
const BlogArticlePage = isWiseUiEnabled() ? WiseArticleBlog : BlogArticle;
const Compare = lazy(() => import('pages/Compare'));
const WiseCompare = lazy(() => import('pages/wise/WiseCompare'));
const ComparePage = isWiseUiEnabled() ? WiseCompare : Compare;
const TraderDashboard = lazy(() => import ('pages/TraderDashboard'));
const TraderPanelClientsList = lazy(() => import ('pages/TraderPanelClientsList'));
const TraderPanelClientCard = lazy(() => import ('pages/TraderPanelClientCard'));
const TraderPanelClientForm = lazy(() => import ('pages/TraderPanelClientForm'));
const TraderPanelClientOffers = lazy(() => import ('pages/TraderPanelClientOffers'));
const TraderPanelClientOffer = lazy(() => import ('pages/TraderPanelClientOffer'));
const TraderPanelClientCarts = lazy(() => import ('pages/TraderPanelClientCarts'));
const TraderPanelClientsCartList = lazy(() => import ('pages/TraderPanelClientsCartList'));
const TraderPanelClientsOrdersList = lazy(() => import ('pages/TraderPanelClientsOrdersList'));
const TraderPanelOpenCart = lazy(() => import ('pages/TraderPanelOpenCart'));
const TraderPanelOpenOffer = lazy(() => import ('pages/TraderPanelOpenOffer'));
const TraderPanelClientOrders = lazy(() => import ('pages/TraderPanelClientOrders'));
const TraderPanelClientDocuments = lazy(() => import ('pages/TraderPanelClientDocuments'));
const TraderPanelClientPayments = lazy(() => import ('pages/TraderPanelClientPayments'));
const TraderPanelClientShoppingLists = lazy(() => import ('pages/TraderPanelClientShoppingLists'));
const TraderPanelProductCard = lazy(() => import ('pages/TraderPanelProductCard'));
const TraderPanelProductsList = lazy(() => import ('pages/TraderPanelProductsList'));
const TraderPanelTasksList = lazy(() => import ('pages/TraderPanelTasksList'));
const TraderPanelTasksForm = lazy(() => import ('pages/TraderPanelTasksForm'));
const TraderPanelCalendar = lazy(() => import ('pages/TraderPanelCalendar'));
const TraderPanelMailboxSettings = lazy(() => import ('pages/TraderPanelMailboxSettings'));
const TraderPanelMailboxInbox = lazy(() => import ('pages/TraderPanelMailboxInbox'));
// S9 fix: uzywamy wariantu Wise z CTA „Inteligentne wyszukiwanie" — wariant zachowuje
// oryginalna strone `pages/TraderPanelOffers/` (klienci na produkcji) niezmieniona.
const TraderPanelOffers = lazy(() => import ('pages/wise/WiseTraderPanelOffers'));
const WiseTraderPanelOfferSemanticSearch = lazy(() => import ('pages/wise/WiseTraderPanelOfferSemanticSearch'));
const TraderInsights = lazy(() => import ('pages/TraderInsights'));
const TraderInsightsClient = lazy(() => import ('pages/TraderInsights/TraderInsightsClient'));
const TraderPanelDealsKanban = lazy(() => import ('pages/TraderPanelDealsKanban/TraderPanelDealsKanban'));
const TraderPanelDealNew = lazy(() => import ('pages/TraderPanelDealNew/TraderPanelDealNew'));
const TraderPanelDealDetail = lazy(() => import ('pages/TraderPanelDealDetail/TraderPanelDealDetail'));
const TraderManagementTradersList = lazy(() => import ('pages/TraderManagementTradersList'));
const TraderManagementTraderSummary = lazy(() => import ('pages/TraderManagementTraderSummary'));

/** Fallback podczas ładowania lazy route – minimalny, bez dodatkowego JS */
const RouteFallback = null;

const Routes = () => {
  const { t } = useTranslation();
  useNotificationToSnackbar();
  const {
    // isProfileLoading,
    isAuthenticated,
    redirectUrl,
    urlPrefix,
    isOpenProfileReady,
    profile,
    overloginUserId,
    notificationModal,
    dismissNotificationModal
  } = useRoutes();

  // Wymuszenie zmiany hasła pomijamy w trybie overlogin (handlowiec wszedl jako klient
  // w kontekscie panelu handlowca - nie powinien ustawiac docelowego hasla klientowi).
  if (profile?.change_password && profile?.role !== 'ROLE_OPEN_PROFILE' && !overloginUserId) {
    return (
      <DefaultLayout>
        <Modal title={t('Ustawienie hasła')}>
          <ChangePasswordForm />
        </Modal>
      </DefaultLayout>
    );
  }

  // Wymuszenie zgod pomijamy w trybie overlogin - klient sam zaakceptuje regulaminy
  // podczas wlasnego, prawdziwego logowania, a nie podczas wizyty handlowca w jego kontekscie.
  if (profile?.consents_required && profile?.role !== 'ROLE_OPEN_PROFILE' && !overloginUserId) {
    return (
      <DefaultLayout>
        <Modal title="" onClose={() => null}>
          <Agreements userId={profile.id} />
        </Modal>
      </DefaultLayout>
    );
  }

  // Bramka bootstrapu open-profile (gość) — defense-in-depth obok prerendera.
  //
  // Główne rozwiązanie problemu indeksowania (SEO): prerender.io w nginx kieruje boty do
  // headless Chrome z timeoutem 20s, który czeka aż SPA załaduje token gościa i wyrenderuje
  // pełną treść. Bot widzi gotowy HTML niezaleznie od tego co frontend pokaże w 1. chwili.
  //
  // Ta bramka jest druga linia obrony — działa dla scenariuszy w których prerender nie pomaga:
  //   * prerender padl / jest restartowany (nginx zwroci 502 botowi, ale dla zwyklych userów
  //     na zimnym starcie i tak zatrzymujemy montowanie zapytan kierujacych do API z 401),
  //   * zwykly user na zimnym starcie — bez bramki widzi przez chwile pusty header i serie
  //     bledow 401 w Network tab.
  //
  // Trzymamy bramke tutaj (nie w MainLayout/DefaultLayout), zeby dotyczyla wszystkich layoutow
  // jednoczesnie — nowy layout dla goscia nie wymaga powtorki logiki.
  //
  // Aktywuje sie WYLACZNIE gdy open-profile jest skonfigurowany i brak tokena.
  // Po porazce loginu goscia useRoutes ustawia isOpenProfileReady=false — bramka sie otwiera,
  // layouty renderuja, dzieci dostaja 401 jak przed zmiana (swiadome — nie ma sensu wisiec).
  //
  // TODO: po potwierdzeniu w prod ze prerender stabilnie obsluguje wszystkie sciezki, mozna
  // rozwazyc usuniecie tej bramki na rzecz prostszej architektury (boty obsluguje wylacznie
  // warstwa infrastruktury, frontend nie ma logiki SEO-aware).
  // Publiczna strona akceptacji oferty (EXTERNAL_ACCEPTANCE) musi byc dostepna BEZ logowania
  // i BEZ czekania na open-profile login. Pominiecie bramki dla /oferta/ pozwala bezposrednio
  // wyrenderowac WisePublicOfferAcceptance (route ponizej) - klient klika link z maila i widzi
  // oferte natychmiast, bez prob logowania goscia w tle.
  const isPublicOfferRoute = window.location.pathname.includes('/oferta/');

  if (isOpenProfileReady && !isAuthenticated && !isPublicOfferRoute) {
    // Pusty wrapper o wymiarach pelnego ekranu (height: 100vh) — rezerwuje miejsce w DOM,
    // dzieki czemu po otwarciu bramki layout wskakuje w gotowa rame zamiast skakac.
    // Lepsze CLS niz zwroт null; bot/Lighthouse widzi stabilny pierwszy paint.
    return <div style={{ width: '100%', height: '100vh' }} />;
  }

  return (
    <>
      <Suspense fallback={RouteFallback}>
        <RouterRoutes>
          {/* Publiczna strona akceptacji oferty (EXTERNAL_ACCEPTANCE) - bez logowania, bez MainLayout.
              Dwa warianty: z prefixem jezyka (/pl/oferta/...) oraz bez (oferta/...) - backend
              moze generowac URL bez jezyka, zaleznie od konfiguracji FRONT_HOST. */}
          <Route
            path={`${urlPrefix}/oferta/:token`}
            element={<WisePublicOfferAcceptance />}
          />
          <Route
            path="/oferta/:token"
            element={<WisePublicOfferAcceptance />}
          />
          <Route element={isAuthenticated ? <MainLayout /> : <DefaultLayout />}>
            <Route path={`${urlPrefix}/login`} element={<LoginPage />} />
            <Route path={`${urlPrefix}/forgot-password`} element={<ForgotPassword />} />
            <Route path={`${urlPrefix}/reset-password/:token`} element={<ResetPassword />} />
          </Route>
          <Route element={<MainLayout />}>
            <Route path={`${urlPrefix}`} element={<Home />} />
            <Route path={`${urlPrefix}/download/export/:hash`} element={<DownloadExport />} />
            {profile?.role !== 'ROLE_OPEN_PROFILE' ? (
              <>
                <Route path={`${urlPrefix}/cart/:id`} element={<CartPage />} />
                <Route path={`${urlPrefix}/checkout/:cartId`} element={<CheckoutPage />} />
                <Route
                  path={`${urlPrefix}/checkout/success/:cartId`}
                  element={<Confirmation cartId={37} />}
                />
                <Route
                  path={`${urlPrefix}/dashboard`}
                  element={<DashboardSubLayoutPage urlPrefix={urlPrefix} />}>
                  <Route index element={<DashboardPage />} />
                  <Route path="carts" element={<CartsPage />} />
                  <Route path="carts/import" element={<CartsImportPage />} />
                  <Route path="clients" element={<Clients />} />
                  <Route path="deliveries" element={<DeliveriesPage />} />
                  <Route path="documents" element={<DocumentsPage />} />
                  <Route path="returns-complaints" element={<ReturnsComplaints />} />
                  <Route path="returns-complaints/:itemId" element={<ReturnComplaint />} />
                  <Route path="export" element={<ExportPage />} />
                  <Route path="faq" element={<Faq />} />
                  <Route path="consent" element={<ConsentPage />} />
                  <Route path="my-account" element={<MyAccountPage />} />
                  <Route path="offers/dedicated" element={<OffersDedicatedPage />} />
                  <Route path="offers/dedicated/:offerId" element={<ClientOfferDetailsPage />} />
                  <Route path="offers/periodic" element={<OffersPeriodicPage />} />
                  <Route path="orders" element={<Orders />} />
                  <Route path="orders/:orderId" element={<OrderPage />} />
                  <Route path="orders-to-approve" element={<WiseCartsToApprove />} />
                  <Route path="orders-to-approve/:id" element={<WiseCartToApprove />} />
                  <Route path="payments" element={<PaymentsPage />} />
                  <Route path="receivers" element={<ReceiversPage />} />
                  <Route path="shopping-lists" element={<ShoppingListsPage />} />
                  <Route path="terms" element={<TermsPage />} />
                  <Route path="users" element={<UsersPage />} />
                  <Route path="company" element={<WiseCompany />} />
                  <Route path="cms/sections" element={<CmsSections />} />
                  <Route path="cms/sections/:id" element={<CmsSection />} />
                  <Route path="cms/articles" element={<CmsArticles />} />
                  <Route path="cms/articles/:id" element={<CmsArticle />} />
                  <Route path="cms/articles/create" element={<CmsArticle />} />
                  <Route path="cms/media" element={<CmsMedia />} />
                  <Route path="*" element={<UrlResolver />} />
                </Route>
              </>
            ) : (
              <>
                <Route path={`${urlPrefix}/cart/:id`} element={<Navigate to="/" replace />} />
                <Route path={`${urlPrefix}/checkout/:cartId`} element={<Navigate to="/" replace />} />
                <Route
                  path={`${urlPrefix}/checkout/success/:cartId`}
                  element={<Navigate to="/" replace />}
                />
                <Route path={`${urlPrefix}/dashboard/*`} element={<Navigate to="/" replace />} />
              </>
            )}
            <Route path={`${urlPrefix}/wip`} element={<WorkInProgress />} />
            <Route path={`${urlPrefix}/knowledge-base`} element={<KnowledgeBaseHomePage />} />
            <Route path={`${urlPrefix}/knowledge-base/all`} element={<KnowledgeBaseAll />} />
            <Route path={`${urlPrefix}/knowledge-base/:id`} element={<KnowledgeBaseArticlePage />} />
            <Route path={`${urlPrefix}/products`} element={<ProductsPage />} />
            <Route path={`${urlPrefix}/products/:id`} element={<ProductPage />} />
            <Route path={`${urlPrefix}/blog/:slug`} element={<BlogArticlePage />} />
            <Route path={`${urlPrefix}/compare`} element={<ComparePage />} />
            <Route path={`${urlPrefix}/register`} element={<RegisterPage />} />
            <Route path="confirm_register" element={<RegisterEmailConfirm />} />
            <Route path={`${urlPrefix}/thankyou_register`} element={<ThankYouRegister />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route path={`${urlPrefix}/payment/status`} element={<PaymentStatus />} />
            <Route path={`${urlPrefix}/*`} element={<UrlResolver />} />
          </Route>
          <Route element={<TraderPanelProtectedRoute />}>
            <Route element={<TraderPanelLayout />}>
              {/* Panel handlowca */}
              <Route path={`${urlPrefix}/trader`}>
                <Route index element={<TraderDashboard />} />
                <Route path="tasks" element={<TraderPanelTasksList />} />
                <Route path="tasks/new" element={<TraderPanelTasksForm />} />
                <Route path="tasks/:id" element={<TraderPanelTasksForm />} />
                <Route path="calendar" element={<TraderPanelCalendar />} />
                <Route path="clients" element={<TraderPanelClientsList />} />
                <Route path="client-carts" element={<TraderPanelClientsCartList />} />
                <Route path="offers" element={<TraderPanelOffers />} />
                <Route path="offers/search" element={<WiseTraderPanelOfferSemanticSearch />} />
                <Route path="sales" element={<TraderPanelClientsOrdersList />} />
                <Route path="cart-open" element={<TraderPanelOpenCart />} />
                <Route path="offer-open" element={<TraderPanelOpenOffer />} />
                <Route path="clients/new" element={<TraderPanelClientForm />} />
                <Route path="clients/:id/edit" element={<TraderPanelClientForm />} />
                <Route path="clients/:id/offers/:offerId" element={<TraderPanelClientOffer />} />
                <Route path="clients/:id/offers" element={<TraderPanelClientOffers />} />
                <Route path="clients/:id/carts" element={<TraderPanelClientCarts />} />
                <Route path="clients/:id/orders" element={<TraderPanelClientOrders />} />
                <Route path="clients/:id/documents" element={<TraderPanelClientDocuments />} />
                <Route path="clients/:id/payments" element={<TraderPanelClientPayments />} />
                <Route path="clients/:id/shopping-lists" element={<TraderPanelClientShoppingLists />} />
                <Route path="clients/:id/calendar" element={<TraderPanelCalendar />} />
                <Route path="clients/:id/products" element={<TraderPanelProductsList />} />
                <Route path="clients/:id/products/:productId" element={<TraderPanelProductCard />} />
                <Route path="products" element={<TraderPanelProductsList />} />
                <Route path="products/:productId" element={<TraderPanelProductCard />} />
                <Route path="clients/:id" element={<TraderPanelClientCard />} />
                <Route path="clients/:id/deals" element={<TraderPanelDealsKanban />} />
                <Route path="insights" element={<TraderInsights />} />
                <Route path="insights/:clientId" element={<TraderInsightsClient />} />
                <Route path="deals" element={<TraderPanelDealsKanban />} />
                <Route path="deals/new" element={<TraderPanelDealNew />} />
                <Route path="deals/:id" element={<TraderPanelDealDetail />} />
                {/* Administracja zespolem handlowcow (tylko ROLE_ADMIN / ROLE_TRADER_MANAGER) */}
                <Route path="management/traders" element={<TraderManagementTradersList />} />
                <Route path="management/traders/:traderId" element={<TraderManagementTraderSummary />} />
                <Route path="settings/mailbox" element={<TraderPanelMailboxSettings />} />
                <Route path="mailbox/inbox" element={<TraderPanelMailboxInbox />} />
                <Route path={'*'} element={<TraderDashboard />} />
              </Route>
            </Route>
          </Route>
          <Route element={<ManagementPanelLayout />}>
            <Route element={<WiseSetupGuard />}>
              <Route path={`${urlPrefix}/managment_panel`}>
                <Route index element={<PanelDashboard />} />
                <Route path="setup" element={<PanelSetup />} />
                <Route path="cms/sections" element={<CmsSections isAdminPanel />} />
                <Route path="cms/sections/:id" element={<CmsSection />} />
                <Route path="cms/articles" element={<CmsArticles isAdminPanel />} />
                <Route path="cms/articles/:id" element={<CmsArticle />} />
                <Route path="cms/articles/create" element={<CmsArticle />} />
                <Route path="cms/media" element={<CmsMedia isAdminPanel />} />
                <Route path="cms/sliders" element={<CmsSliders />} />
                <Route path="cms/sliders/create" element={<CmsSlider />} />
                <Route path="cms/sliders/:id" element={<CmsSlider />} />
                <Route path="products/import" element={<PanelProductsImport />} />
                <Route path="pages" element={<PanelPages />} />
                <Route path="blog" element={<PanelBlog />} />
                <Route path="blog/:articleId" element={<PanelBlog />} />
                <Route path="dynamic_page/:pageSymbol" element={<PanelDynamicPage />} />
              </Route>
            </Route>
          </Route>
          <Route path="*" element={<Navigate to={redirectUrl} replace />} />
        </RouterRoutes>
      </Suspense>

      {notificationModal && (
        <Modal title={notificationModal.title} onClose={dismissNotificationModal}>
          <div dangerouslySetInnerHTML={{ __html: notificationModal.content }} />
        </Modal>
      )}

      <NewVersionPopup />
    </>
  );
};

export default Routes;
